import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

type MealType = 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK' | 'DRINK' | 'OTHER';
type Stoplight = 'GREEN' | 'YELLOW' | 'RED';

export interface LoggedMealItem {
  customName: string;
  stoplightClassification: Stoplight;
}

export interface LoggedMealRecord {
  id: string;
  timestamp: string;
  mealType: MealType;
  description: string;
  items: LoggedMealItem[];
}

interface MealLoggingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (meal: LoggedMealRecord) => void;
}

interface EditableMealItem extends LoggedMealItem {
  id: string;
}

const steps = ['Capture meal photo', 'Analyze photo', 'Review and save'];

const defaultSuggestionsByMealType: Record<MealType, EditableMealItem[]> = {
  BREAKFAST: [
    { id: 'suggestion-1', customName: 'Oatmeal', stoplightClassification: 'GREEN' },
    { id: 'suggestion-2', customName: 'Berries', stoplightClassification: 'GREEN' },
  ],
  LUNCH: [
    { id: 'suggestion-1', customName: 'Grilled chicken', stoplightClassification: 'GREEN' },
    { id: 'suggestion-2', customName: 'Rice', stoplightClassification: 'YELLOW' },
  ],
  DINNER: [
    { id: 'suggestion-1', customName: 'Salmon', stoplightClassification: 'GREEN' },
    { id: 'suggestion-2', customName: 'Roasted vegetables', stoplightClassification: 'GREEN' },
  ],
  SNACK: [
    { id: 'suggestion-1', customName: 'Greek yogurt', stoplightClassification: 'GREEN' },
    { id: 'suggestion-2', customName: 'Granola', stoplightClassification: 'YELLOW' },
  ],
  DRINK: [
    { id: 'suggestion-1', customName: 'Coffee', stoplightClassification: 'GREEN' },
    { id: 'suggestion-2', customName: 'Milk', stoplightClassification: 'YELLOW' },
  ],
  OTHER: [{ id: 'suggestion-1', customName: 'Meal item', stoplightClassification: 'YELLOW' }],
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const MealLoggingModal = ({ open, onClose, onSave }: MealLoggingModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [mealType, setMealType] = useState<MealType>('BREAKFAST');
  const [capturedPhoto, setCapturedPhoto] = useState<File | null>(null);
  const [capturedAt, setCapturedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [wasAnalyzed, setWasAnalyzed] = useState(false);
  const [items, setItems] = useState<EditableMealItem[]>([]);

  const photoPreviewUrl = useMemo(
    () => (capturedPhoto ? URL.createObjectURL(capturedPhoto) : null),
    [capturedPhoto]
  );

  const resetModal = () => {
    setActiveStep(0);
    setMealType('BREAKFAST');
    setCapturedPhoto(null);
    setCapturedAt(new Date().toISOString().slice(0, 16));
    setIsAnalyzing(false);
    setWasAnalyzed(false);
    setItems([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    await new Promise((resolve) => setTimeout(resolve, 900));
    setItems(defaultSuggestionsByMealType[mealType].map((item) => ({ ...item, id: genId() })));
    setWasAnalyzed(true);
    setIsAnalyzing(false);
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      { id: genId(), customName: '', stoplightClassification: 'YELLOW' },
    ]);
  };

  const handleDeleteItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = () => {
    const cleanItems = items
      .map((item) => ({
        customName: item.customName.trim(),
        stoplightClassification: item.stoplightClassification,
      }))
      .filter((item) => item.customName.length > 0);

    if (cleanItems.length === 0) {
      return;
    }

    onSave({
      id: `meal-${genId()}`,
      timestamp: new Date(capturedAt).toISOString(),
      mealType,
      description: cleanItems.map((item) => item.customName).join(', '),
      items: cleanItems,
    });

    handleClose();
  };

  const canMoveNext =
    (activeStep === 0 && Boolean(capturedPhoto)) ||
    (activeStep === 1 && wasAnalyzed) ||
    activeStep === 2;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Log Meal</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Meal type</InputLabel>
              <Select
                value={mealType}
                label="Meal type"
                onChange={(event) => setMealType(event.target.value as MealType)}
              >
                <MenuItem value="BREAKFAST">Breakfast</MenuItem>
                <MenuItem value="LUNCH">Lunch</MenuItem>
                <MenuItem value="DINNER">Dinner</MenuItem>
                <MenuItem value="SNACK">Snack</MenuItem>
                <MenuItem value="DRINK">Drink</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="datetime-local"
              label="When did you eat?"
              InputLabelProps={{ shrink: true }}
              value={capturedAt}
              onChange={(event) => setCapturedAt(event.target.value)}
            />

            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="meal-photo-upload"
                type="file"
                capture="environment"
                onChange={(event) => setCapturedPhoto(event.target.files?.[0] ?? null)}
              />
              <label htmlFor="meal-photo-upload">
                <Button variant="outlined" component="span" startIcon={<PhotoCameraIcon />}>
                  {capturedPhoto ? 'Replace Meal Photo' : 'Take Meal Photo'}
                </Button>
              </label>
            </Box>

            {photoPreviewUrl && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Photo preview
                </Typography>
                <Box
                  component="img"
                  src={photoPreviewUrl}
                  alt="Meal preview"
                  sx={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 1 }}
                />
              </Box>
            )}
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Analyze your meal photo to pre-fill meal items. You can edit all detected items before saving.
            </Typography>
            <Button variant="contained" onClick={handleAnalyze} disabled={isAnalyzing}>
              {isAnalyzing ? 'Analyzing photo...' : 'Analyze Meal Photo'}
            </Button>
            {wasAnalyzed && (
              <Typography variant="body2" color="success.main">
                Analysis complete. Continue to review detected meal items.
              </Typography>
            )}
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Detected Meal Items</Typography>
              <Button startIcon={<AddIcon />} onClick={handleAddItem}>
                Add item
              </Button>
            </Stack>

            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No items yet. Add at least one meal item before saving.
              </Typography>
            )}

            {items.map((item) => (
              <Stack key={item.id} direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  label="Meal item"
                  value={item.customName}
                  onChange={(event) =>
                    setItems((prev) =>
                      prev.map((current) =>
                        current.id === item.id
                          ? { ...current, customName: event.target.value }
                          : current
                      )
                    )
                  }
                />
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={item.stoplightClassification}
                    label="Class"
                    onChange={(event) =>
                      setItems((prev) =>
                        prev.map((current) =>
                          current.id === item.id
                            ? {
                                ...current,
                                stoplightClassification: event.target.value as Stoplight,
                              }
                            : current
                        )
                      )
                    }
                  >
                    <MenuItem value="GREEN">Green</MenuItem>
                    <MenuItem value="YELLOW">Yellow</MenuItem>
                    <MenuItem value="RED">Red</MenuItem>
                  </Select>
                </FormControl>
                <IconButton onClick={() => handleDeleteItem(item.id)} aria-label="remove meal item">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={() => setActiveStep((prev) => prev - 1)} disabled={activeStep === 0}>
          Back
        </Button>
        {activeStep < 2 ? (
          <Button variant="contained" onClick={() => setActiveStep((prev) => prev + 1)} disabled={!canMoveNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!items.some((item) => item.customName.trim().length > 0)}
          >
            Save Meal
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
